import type { Position } from '@xyflow/react';
import {
  getBezierPath,
  getStraightPath,
  getSmoothStepPath,
} from '@xyflow/react';

import { EEdgeType } from './types';

interface GetEdgePathParams {
  sourceX: number;
  sourceY: number;
  sourcePosition: Position;
  targetX: number;
  targetY: number;
  targetPosition: Position;
  edgeType: EEdgeType;
  borderRadius?: number;
}

export function getEdgePath(
  params: GetEdgePathParams,
): [string, number, number, number, number] {
  const {
    sourceX,
    sourceY,
    sourcePosition,
    targetX,
    targetY,
    targetPosition,
    edgeType,
    borderRadius = 5,
  } = params;

  switch (edgeType) {
    case EEdgeType.STRAIGHT:
      return getStraightPath({ sourceX, sourceY, targetX, targetY });

    case EEdgeType.SMOOTHSTEP:
      return getSmoothStepPath({
        sourceX,
        sourceY,
        sourcePosition,
        targetX,
        targetY,
        targetPosition,
        borderRadius,
      });

    case EEdgeType.BEZIER:
    default:
      return getBezierPath({
        sourceX,
        sourceY,
        sourcePosition,
        targetX,
        targetY,
        targetPosition,
      });
  }
}

// ─── Public API ───────────────────────────────────────────────────────────────

export interface CreateOffsetPathSmoothStepParams {
  sourceX: number;
  sourceY: number;
  sourcePosition: Position;
  targetX: number;
  targetY: number;
  targetPosition: Position;
  borderRadius?: number;
}

/**
 * Create a path parallel to `originalPath`, shifted by `offset` pixels.
 *
 * SmoothStep strategy:
 *   1. Shift sourceX/Y and targetX/Y perpendicularly by `offset`.
 *   2. Adjust borderRadius so the arc stays CONCENTRIC with the original
 *      (outer arc larger, inner arc smaller — standard railway / road geometry).
 *
 * Bezier / Straight: normal-vector sampling (no corner discontinuity).
 */
export function createOffsetPath(
  originalPath: string,
  offset: number,
  edgeType?: EEdgeType,
  _smoothStepParams?: CreateOffsetPathSmoothStepParams,
): string {
  if (offset === 0 && edgeType !== EEdgeType.SMOOTHSTEP) return originalPath;

  if (edgeType === EEdgeType.SMOOTHSTEP) {
    return sampleOffsetPath(originalPath, offset, 120);
  }

  return sampleOffsetPath(originalPath, offset);
}

// ─── Sampling-based offset (Bezier / Straight) ────────────────────────────────

function samplePath(
  pathEl: SVGPathElement,
  samples = 100,
): { x: number; y: number }[] {
  const total = pathEl.getTotalLength();
  const pts: { x: number; y: number }[] = [];
  for (let i = 0; i <= samples; i += 1) {
    const pt = pathEl.getPointAtLength((i / samples) * total);
    pts.push({ x: pt.x, y: pt.y });
  }
  return pts;
}

function normalAt(
  pts: { x: number; y: number }[],
  i: number,
): { nx: number; ny: number } {
  const prev = pts[Math.max(0, i - 1)];
  const next = pts[Math.min(pts.length - 1, i + 1)];
  const dx = next.x - prev.x;
  const dy = next.y - prev.y;
  const len = Math.sqrt(dx * dx + dy * dy) || 1;
  return { nx: -dy / len, ny: dx / len };
}

function sampleOffsetPath(
  originalPath: string,
  offset: number,
  samples = 80,
): string {
  const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
  svg.style.position = 'absolute';
  svg.style.visibility = 'hidden';
  document.body.appendChild(svg);

  const pathEl = document.createElementNS('http://www.w3.org/2000/svg', 'path');
  pathEl.setAttribute('d', originalPath);
  svg.appendChild(pathEl);

  const pts = samplePath(pathEl, samples);
  document.body.removeChild(svg);

  const offsetPts = pts.map((pt, i) => {
    const { nx, ny } = normalAt(pts, i);
    return { x: pt.x + nx * offset, y: pt.y + ny * offset };
  });

  return pointsToSmoothPath(smoothPoints(offsetPts, 2));
}

function smoothPoints(
  pts: { x: number; y: number }[],
  iterations = 1,
): { x: number; y: number }[] {
  if (pts.length < 3 || iterations <= 0) return pts;

  let result = pts;
  for (let iter = 0; iter < iterations; iter += 1) {
    const next = [result[0]];
    for (let i = 1; i < result.length - 1; i += 1) {
      const prev = result[i - 1];
      const curr = result[i];
      const after = result[i + 1];
      next.push({
        x: (prev.x + curr.x * 2 + after.x) / 4,
        y: (prev.y + curr.y * 2 + after.y) / 4,
      });
    }
    next.push(result[result.length - 1]);
    result = next;
  }

  return result;
}

function pointsToSmoothPath(pts: { x: number; y: number }[]): string {
  if (pts.length < 2) return '';
  let d = `M ${pts[0].x} ${pts[0].y}`;

  for (let i = 1; i < pts.length - 1; i += 1) {
    const curr = pts[i];
    const next = pts[i + 1];
    const midX = (curr.x + next.x) / 2;
    const midY = (curr.y + next.y) / 2;
    d += ` Q ${curr.x} ${curr.y}, ${midX} ${midY}`;
  }

  const last = pts[pts.length - 1];
  const beforeLast = pts[pts.length - 2];
  d += ` Q ${beforeLast.x} ${beforeLast.y}, ${last.x} ${last.y}`;
  return d;
}
